import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { auth, AppUser } from '../config/firebase';
import { apiService } from '../services/apiService';

interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: 'student' | 'employer';
  profilePicture?: string;
  // Additional fields based on user type
  [key: string]: any;
}

interface AuthContextType {
  currentUser: AppUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  getToken: () => Promise<string | null>;
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  userType: 'student' | 'employer';
  companyName?: string;
  companySize?: string;
  industry?: string;
  university?: string;
  major?: string;
  graduationYear?: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

/** Pull a human-readable message out of an axios/native error. */
function messageFrom(error: any, fallback: string): string {
  return error?.response?.data?.message || error?.message || fallback;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AppUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  /** Store the JWT + identity and update React state from a login/register response. */
  const applySession = (token: string, user: UserProfile): void => {
    auth.setSession(token, user);
    setCurrentUser(auth.currentUser);
    setUserProfile(user);
  };

  const login = async (email: string, password: string): Promise<void> => {
    try {
      const response = await apiService.post('auth/login', { email, password });
      const { token, user } = response.data.data || {};
      if (!response.data.success || !token) {
        throw new Error(response.data.message || 'Login failed');
      }
      applySession(token, user);
    } catch (error: any) {
      console.error('Login error:', error);
      throw new Error(messageFrom(error, 'Invalid email or password'));
    }
  };

  const register = async (userData: RegisterData): Promise<void> => {
    try {
      const response = await apiService.post('auth/register', userData);
      const { token, user } = response.data.data || {};
      if (!response.data.success || !token) {
        throw new Error(response.data.message || 'Registration failed');
      }
      applySession(token, user);
    } catch (error: any) {
      console.error('Registration error:', error);
      throw new Error(messageFrom(error, 'Registration failed. Please try again.'));
    }
  };

  const logout = async (): Promise<void> => {
    await auth.signOut();
    setCurrentUser(null);
    setUserProfile(null);
  };

  const getToken = async (): Promise<string | null> => {
    return localStorage.getItem('authToken');
  };

  const fetchUserProfile = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUserProfile(null);
      setCurrentUser(null);
      return;
    }
    try {
      const response = await apiService.get('auth/profile', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        setUserProfile(response.data.data);
        setCurrentUser(auth.currentUser);
      } else {
        await auth.signOut();
        setUserProfile(null);
        setCurrentUser(null);
      }
    } catch (error) {
      console.error('Fetch profile error:', error);
      // Token invalid/expired — clear it so the app renders the logged-out state.
      await auth.signOut();
      setUserProfile(null);
      setCurrentUser(null);
    }
  };

  const updateProfile = async (data: Partial<UserProfile>): Promise<void> => {
    try {
      const token = await getToken();
      if (!token) throw new Error('No authentication token');

      const response = await apiService.put('auth/profile', data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setUserProfile(response.data.data);
      } else {
        throw new Error(response.data.message || 'Profile update failed');
      }
    } catch (error: any) {
      console.error('Update profile error:', error);
      throw new Error(messageFrom(error, 'Profile update failed'));
    }
  };

  // On mount, restore the session from any stored JWT.
  useEffect(() => {
    (async () => {
      await fetchUserProfile();
      setLoading(false);
    })();
  }, []);

  const value: AuthContextType = {
    currentUser,
    userProfile,
    loading,
    login,
    register,
    logout,
    getToken,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
